/**
 * Checkout.com Frames Configuration
 *
 * Gateway settings and configuration for credit card form.
 */

import type { GatewaySettings } from '../../types';
import { PURCHASE_FLOW_TEXT_KEYS } from '../../utils/constants';

// ============================================================================
// Types
// ============================================================================

/** Text resolver — same signature as renderText from purchase flow */
type TextResolver = (key: string, params?: Record<string, string | number>) => string;

export interface CheckoutFramesLabels {
  cName: string;
  ccn: string;
  exp: string;
  cvv: string;
  cZip: string;
}

export interface CheckoutFramesPlaceholders {
  cName: string;
  ccn: string;
  cvv: string;
  exp: string;
  cZip: string;
}

// ============================================================================
// Configuration Factory
// ============================================================================

/**
 * Create gateway settings for Checkout.com Frames
 * @param renderText - Text resolver function (renderText from purchase flow)
 */
export const createGatewaySettings = (renderText: TextResolver): GatewaySettings => ({
  needShowCreditCardImg: true,
  inputLabels: {
    cName: renderText(PURCHASE_FLOW_TEXT_KEYS.CC_LABEL_NAME),
    ccn: renderText(PURCHASE_FLOW_TEXT_KEYS.CC_LABEL_CARD_NUMBER),
    exp: renderText(PURCHASE_FLOW_TEXT_KEYS.CC_LABEL_EXPIRY),
    cvv: renderText(PURCHASE_FLOW_TEXT_KEYS.CC_LABEL_CVV),
    cZip: renderText(PURCHASE_FLOW_TEXT_KEYS.CC_LABEL_ZIP)
  },
  placeHolders: {},
  tooltips: {
    exp: { text: renderText(PURCHASE_FLOW_TEXT_KEYS.CC_TOOLTIP_EXPIRY) },
    cvv: { text: renderText(PURCHASE_FLOW_TEXT_KEYS.CC_TOOLTIP_CVV) }
  }
  // styling now lives in classes.module.scss and reads --app-* vars directly;
  // the legacy --blue-snap-* indirection layer is gone.
});

/**
 * Get placeholders for Checkout.com Frames initialization
 * @param settings - Gateway settings with placeholders
 */
export const getFramesPlaceholders = (settings: GatewaySettings) => ({
  cardNumber: settings.placeHolders?.ccn || ' ',
  cvv: settings.placeHolders?.cvv || ' ',
  expiryMonth: settings.placeHolders?.exp?.split('/')?.[0] || ' ',
  expiryYear: settings.placeHolders?.exp?.split('/')?.[1] || ' '
});
